import React, { useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Popover, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaletteIcon from '@mui/icons-material/Palette';
import SaveIcon from '@mui/icons-material/Save';
import * as ReactColor from 'react-color';
import { DEFAULT_API_ADMIN } from '../../../constants';
import { getChainId, httpClientReactAdmin, moveItemInArray } from '../../../lib';
import {
  MAX_SEGMENTS, MAX_SEGMENT_LABEL_LENGTH, RECOMMENDED_MAX_SEGMENTS, RoletaSegment, RoletaSegmentKind, RoletaStatus,
} from '../../../models/roleta';
import { readableInk, segmentColor } from '../wheelGeometry';

const ChromePicker: any = (ReactColor as any).ChromePicker;

export interface PrizeProductOption {
  id: string;
  label: string;
}

interface Props {
  roletaId: string;
  roletaStatus: RoletaStatus;
  segments: RoletaSegment[];
  onChange: (segments: RoletaSegment[]) => void;
  /** Produtos do brinde de prêmios da roleta (regra "Prêmio da Roleta"). */
  prizeProducts: PrizeProductOption[];
  loadingPrizeProducts: boolean;
  /** Cor do app da rede — base da paleta automática. */
  baseColor: string;
  onSaved?: () => void;
}

/** Fatia nova em branco. `id` só existe depois de salvar. */
const blankSegment = (index: number): RoletaSegment => ({
  segmentIndex: index,
  kind: RoletaSegmentKind.Prize,
  label: '',
  color: null,
  icon: null,
  weight: 1,
  giftProductId: null,
  giftProductName: null,
  maxWins: null,
  winsCount: 0,
  maxWinsPerCustomer: null,
  isEnabled: true,
  estimatedCost: null,
});

/** Reindexa `segmentIndex` pela posição atual — é o que o PUT espera. */
const reindex = (segments: RoletaSegment[]): RoletaSegment[] =>
  segments.map((segment, index) => ({ ...segment, segmentIndex: index }));

const toNumberOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Validação no cliente. Erro bloqueia o salvamento; aviso só orienta.
 * Textos fixados em .planning/roleta/03-UX-ADMIN.md §4.2.
 */
export const validateSegments = (segments: RoletaSegment[]): { errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const enabled = segments.filter((segment) => segment.isEnabled);

  if (enabled.length < 2) {
    errors.push('Cadastre pelo menos 2 fatias.');
  }
  if (enabled.some((segment) => segment.kind === RoletaSegmentKind.Prize && !segment.giftProductId)) {
    errors.push('Toda fatia de prêmio precisa de um prêmio selecionado.');
  }
  if (enabled.length > 0 && !enabled.some((segment) => segment.maxWins === null || segment.maxWins === undefined)) {
    errors.push('Pelo menos uma fatia precisa ter estoque ilimitado — senão a roleta pode ficar sem opção.');
  }
  if (segments.some((segment) => !Number.isFinite(segment.weight) || segment.weight < 1)) {
    errors.push('O peso precisa ser 1 ou maior.');
  }
  if (segments.some((segment) => !segment.label.trim())) {
    errors.push('Toda fatia precisa de um rótulo.');
  }
  if (segments.some((segment) => segment.label.length > MAX_SEGMENT_LABEL_LENGTH)) {
    errors.push(`Rótulo com mais de ${MAX_SEGMENT_LABEL_LENGTH} caracteres fica ilegível no celular.`);
  }
  if (enabled.length > RECOMMENDED_MAX_SEGMENTS) {
    warnings.push(`Acima de ${RECOMMENDED_MAX_SEGMENTS} fatias o texto some no celular.`);
  }

  return { errors, warnings };
};

const SegmentColorButton: React.FC<{
  value: string | null | undefined;
  effective: string;
  onChange: (color: string | null) => void;
  disabled?: boolean;
}> = ({ value, effective, onChange, disabled }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title={value ? 'Trocar a cor da fatia' : 'Cor automática da rede — clique para escolher'}>
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={(event) => setAnchor(event.currentTarget)}
            sx={{
              width: 30, height: 30, borderRadius: 1,
              bgcolor: effective, color: readableInk(effective),
              border: '1px solid', borderColor: 'divider',
              '&:hover': { bgcolor: effective, opacity: 0.85 },
            }}
          >
            <PaletteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1 }}>
          {ChromePicker && (
            <ChromePicker
              disableAlpha
              color={effective}
              onChange={(color: any) => onChange(color?.hex ?? null)}
            />
          )}
          <Button size="small" fullWidth sx={{ mt: 1 }} onClick={() => { onChange(null); setAnchor(null); }}>
            Usar cor automática
          </Button>
        </Box>
      </Popover>
    </>
  );
};

const SegmentEditor: React.FC<Props> = ({
  roletaId, roletaStatus, segments, onChange, prizeProducts, loadingPrizeProducts, baseColor, onSaved,
}) => {
  const notify = useNotify();
  const chainId = getChainId();
  const [saving, setSaving] = useState(false);

  const readOnly = roletaStatus === RoletaStatus.Closed;

  const totalWeight = useMemo(
    () => segments.filter((segment) => segment.isEnabled).reduce((sum, segment) => sum + Math.max(0, segment.weight || 0), 0),
    [segments],
  );

  const { errors, warnings } = useMemo(() => validateSegments(segments), [segments]);

  const patch = (index: number, changes: Partial<RoletaSegment>) => {
    onChange(segments.map((segment, i) => (i === index ? { ...segment, ...changes } : segment)));
  };

  const handleAdd = () => {
    if (segments.length >= MAX_SEGMENTS) {
      notify(`A roda comporta no máximo ${MAX_SEGMENTS} fatias.`, { type: 'warning' });
      return;
    }
    onChange(reindex([...segments, blankSegment(segments.length)]));
  };

  const handleRemove = (index: number) => {
    const segment = segments[index];
    if ((segment.winsCount ?? 0) > 0) {
      notify('Esta fatia já foi sorteada — desative em vez de remover, para não perder o histórico.', { type: 'warning' });
      return;
    }
    onChange(reindex(segments.filter((_, i) => i !== index)));
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const segment = segments[index];
    const moved = direction === 'up'
      ? moveItemInArray.moveUp(segments, segment)
      : moveItemInArray.moveDown(segments, segment);
    onChange(reindex(moved as RoletaSegment[]));
  };

  const handleResetColors = () => {
    onChange(segments.map((segment) => ({ ...segment, color: null })));
    notify('Cores automáticas restauradas. Salve as fatias para valer.', { type: 'info' });
  };

  const handleKindChange = (index: number, kind: RoletaSegmentKind) => {
    // Trocar o tipo zera o que não existe no tipo novo: prêmio, estoque e teto
    // por CPF só fazem sentido em fatia de prêmio.
    if (kind === RoletaSegmentKind.Prize) {
      patch(index, { kind });
      return;
    }
    patch(index, {
      kind,
      giftProductId: null,
      giftProductName: null,
      maxWins: null,
      maxWinsPerCustomer: null,
      estimatedCost: null,
    });
  };

  const handleSave = async () => {
    if (errors.length) {
      notify('Corrija os avisos em vermelho antes de salvar as fatias.', { type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        segments: reindex(segments).map((segment) => ({
          ...(segment.id ? { id: segment.id } : {}),
          segmentIndex: segment.segmentIndex,
          kind: segment.kind,
          label: segment.label.trim(),
          color: segment.color || null,
          icon: segment.icon || null,
          weight: segment.weight,
          giftProductId: segment.kind === RoletaSegmentKind.Prize ? segment.giftProductId ?? null : null,
          maxWins: segment.maxWins ?? null,
          maxWinsPerCustomer: segment.maxWinsPerCustomer ?? null,
          estimatedCost: segment.estimatedCost ?? null,
          isEnabled: segment.isEnabled,
        })),
      };

      const url = `${DEFAULT_API_ADMIN}/chains/${chainId}/roletas/${roletaId}/segments`;
      const { json } = await httpClientReactAdmin(url, { method: 'PUT', body: JSON.stringify(payload) });
      const saved = (json?.data ?? json) as { segments?: RoletaSegment[] } | RoletaSegment[];
      const savedSegments = Array.isArray(saved) ? saved : saved?.segments;
      if (savedSegments?.length) {
        onChange(reindex(savedSegments));
      }
      notify('Fatias salvas.', { type: 'success' });
      onSaved?.();
    } catch (err: any) {
      const apiMessage = err?.body?.message || err?.body?.error || err?.message || 'Erro ao salvar as fatias';
      notify(apiMessage, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const noPrizeProducts = !loadingPrizeProducts && prizeProducts.length === 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} mb={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Fatias da roda</Typography>
          <Typography variant="caption" color="text.secondary">
            Todas as fatias têm o mesmo tamanho na tela. Quem manda na chance é o peso — o cliente nunca vê esse número.
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} disabled={readOnly}>
            Adicionar fatia
          </Button>
          <Button size="small" startIcon={<PaletteIcon />} onClick={handleResetColors} disabled={readOnly}>
            Restaurar cores automáticas
          </Button>
        </Stack>
      </Stack>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Roleta encerrada: as fatias ficam só para consulta.
        </Alert>
      )}

      {noPrizeProducts && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nenhum prêmio disponível. Cadastre o <strong>brinde de prêmios</strong> desta roleta no Clube de Vantagens
          (regra "Prêmio da Roleta") e volte aqui para escolher o prêmio de cada fatia.{' '}
          <Button size="small" href="#/gifts/create">Criar brinde de prêmios</Button>
        </Alert>
      )}

      {errors.map((message) => (
        <Alert key={message} severity="error" sx={{ mb: 1 }}>{message}</Alert>
      ))}
      {warnings.map((message) => (
        <Alert key={message} severity="warning" sx={{ mb: 1 }}>{message}</Alert>
      ))}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 76 }}>Ordem</TableCell>
              <TableCell sx={{ minWidth: 190 }}>Rótulo</TableCell>
              <TableCell sx={{ minWidth: 170 }}>Tipo</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Prêmio</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Peso</TableCell>
              <TableCell sx={{ minWidth: 140 }}>Estoque</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Por cliente</TableCell>
              <TableCell sx={{ width: 60 }}>Cor</TableCell>
              <TableCell sx={{ width: 80 }}>Ícone</TableCell>
              <TableCell sx={{ width: 70 }}>Ativa</TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {segments.length === 0 && (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Nenhuma fatia ainda. Comece com 4 a 6 fatias: 2 ou 3 prêmios, 1 "Gire de novo" e 1 "Não foi dessa vez".
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {segments.map((segment, index) => {
              const effectiveColor = segmentColor(segment.color, baseColor, index, Math.max(1, segments.length));
              const isPrize = segment.kind === RoletaSegmentKind.Prize;
              const chancePct = segment.isEnabled && totalWeight > 0
                ? (Math.max(0, segment.weight || 0) / totalWeight)
                : 0;
              const alreadyDrawn = (segment.winsCount ?? 0) > 0;
              const remaining = segment.maxWins !== null && segment.maxWins !== undefined
                ? Math.max(0, segment.maxWins - (segment.winsCount ?? 0))
                : null;

              return (
                <TableRow key={segment.id ?? `new-${index}`} hover>
                  <TableCell>
                    <Stack direction="row">
                      <IconButton size="small" disabled={readOnly || index === 0} onClick={() => handleMove(index, 'up')}>
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" disabled={readOnly || index === segments.length - 1} onClick={() => handleMove(index, 'down')}>
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      disabled={readOnly}
                      value={segment.label}
                      onChange={(event) => patch(index, { label: event.target.value.slice(0, MAX_SEGMENT_LABEL_LENGTH) })}
                      inputProps={{ maxLength: MAX_SEGMENT_LABEL_LENGTH }}
                      helperText={`${segment.label.length}/${MAX_SEGMENT_LABEL_LENGTH}`}
                      error={!segment.label.trim()}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      disabled={readOnly}
                      value={segment.kind}
                      onChange={(event) => handleKindChange(index, event.target.value as RoletaSegmentKind)}
                    >
                      {RoletaSegmentKind.roletaSegmentKindChoices.map((choice) => (
                        <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell>
                    {isPrize ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        disabled={readOnly || loadingPrizeProducts || prizeProducts.length === 0}
                        value={segment.giftProductId ?? ''}
                        onChange={(event) => {
                          const selected = prizeProducts.find((product) => product.id === event.target.value);
                          patch(index, {
                            giftProductId: selected?.id ?? null,
                            giftProductName: selected?.label ?? null,
                          });
                        }}
                        error={!segment.giftProductId}
                        helperText={segment.giftProductId ? undefined : 'Escolha o prêmio'}
                      >
                        {prizeProducts.map((product) => (
                          <MenuItem key={product.id} value={product.id}>{product.label}</MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {segment.kind === RoletaSegmentKind.ExtraSpin
                          ? 'Devolve um giro novo ao cliente'
                          : 'Fatia sem prêmio'}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      disabled={readOnly}
                      value={segment.weight ?? 1}
                      onChange={(event) => patch(index, { weight: Math.max(1, parseInt(event.target.value, 10) || 1) })}
                      inputProps={{ min: 1, step: 1 }}
                      sx={{ width: 84 }}
                      helperText={segment.isEnabled
                        ? `${(chancePct * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% de chance`
                        : 'Fatia desativada'}
                    />
                  </TableCell>

                  <TableCell>
                    {isPrize ? (
                      <>
                        <TextField
                          size="small"
                          type="number"
                          disabled={readOnly}
                          value={segment.maxWins ?? ''}
                          onChange={(event) => patch(index, { maxWins: toNumberOrNull(event.target.value) })}
                          inputProps={{ min: 0, step: 1 }}
                          placeholder="Ilimitado"
                          sx={{ width: 110 }}
                          helperText={segment.maxWins === null || segment.maxWins === undefined ? 'Ilimitado' : undefined}
                        />
                        {remaining !== null && (
                          <Chip size="small" sx={{ mt: 0.5 }} label={`restam ${remaining}`} color={remaining === 0 ? 'error' : 'default'} />
                        )}
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Ilimitado</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {isPrize ? (
                      <Tooltip title="Máximo de vezes que o mesmo CPF ganha esta fatia">
                        <TextField
                          size="small"
                          type="number"
                          disabled={readOnly}
                          value={segment.maxWinsPerCustomer ?? ''}
                          onChange={(event) => patch(index, { maxWinsPerCustomer: toNumberOrNull(event.target.value) })}
                          inputProps={{ min: 1, step: 1 }}
                          placeholder="Sem teto"
                          sx={{ width: 100 }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <SegmentColorButton
                      value={segment.color}
                      effective={effectiveColor}
                      disabled={readOnly}
                      onChange={(color) => patch(index, { color })}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      disabled={readOnly}
                      value={segment.icon ?? ''}
                      onChange={(event) => patch(index, { icon: Array.from(event.target.value).slice(0, 2).join('') || null })}
                      placeholder="🎁"
                      sx={{ width: 64 }}
                    />
                  </TableCell>

                  <TableCell>
                    <Switch
                      size="small"
                      disabled={readOnly}
                      checked={segment.isEnabled}
                      onChange={(event) => patch(index, { isEnabled: event.target.checked })}
                    />
                  </TableCell>

                  <TableCell>
                    <Tooltip title={alreadyDrawn ? 'Fatia já sorteada — desative em vez de remover' : 'Remover fatia'}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={readOnly || alreadyDrawn}
                          onClick={() => handleRemove(index)}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" gap={1} mt={2}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={readOnly || saving || errors.length > 0}
          onClick={handleSave}
        >
          Salvar fatias
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
        As fatias são salvas separadamente do resto da roleta.
      </Typography>
    </Paper>
  );
};

export default SegmentEditor;
